import axios from "axios";
import { getTokenHeader } from "@/helpers/commonHelper";
import { uploadFile } from "@/helpers/fileHelper";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

const BASE = `${env.prompts_api_client_url}api/eo-prompt/pipelines`;

export default {
  name: "RAGManagement",
  data() {
    return {
      pipelines: [],
      pipelineGroups: [], // Gruppenliste
      selectedGroup: null, // aktuell gewählte Gruppe
      newGroupName: "", // Name für neue Gruppe
      newGroupPromptShortId: null, // Neue Prompt-Auswahl für Gruppe
      prompts: [], // Liste aller verfügbaren Prompts
      promptsLoading: false, // Lade-Status für Prompts
      typeOptions: [
        { value: "website", text: "Webseite" },
        { value: "rss-feed", text: "RSS Feed" },
        { value: "eo-news", text: "EO-Nachrichten" },
        { value: "eo-pois", text: "EO-POIs" },
        { value: "eo-events", text: "EO-Events" },
        { value: "eo-kis", text: "EO-KIS" },
        { value: "tomas", text: "TOMAS" },
        { value: "documents", text: "Dokumente" }
      ],
      form: {
        name: "",
        description: "",
        type: "",
        sitemap: "",
        includeURLsText: "",
        rssFeed: "",
        boost: 0.0,
        cj_enabled: false,
        eoNewsApiKey: "",
        newsBaseURL: "",
        poiApiKey: "",
        poiBaseURL: "",
        eventsApiKey: "",
        eventsBaseURL: "",
        kisApiKey: "",
        clubBaseURL: "",
        committeeBaseURL: "",
        departmentBaseURL: "",
        facilityBaseURL: "",
        personBaseURL: "",
        taskBaseURL: "",
        tportalSourceURL: "",
        thomasBaseURL: "",
        documents: [],
        pipeline_group: null // Gruppe für Pipeline
      },
      isEditing: false,
      editingId: null,
      deleteDialog: false,
      pipelineToDelete: null,
      showForm: false,
      showGroupDialog: false, // Dialog für neue Gruppe
      deleteGroupDialog: false, // Dialog für Gruppenlöschung
      groupToDelete: null, // Gruppe zum Löschen
      activeGroup: null,
      questionText: "",
      questionResults: [],
      questionError: "",
      questionLoading: false,
      questionDialog: false,
      questionAnswer: "",

      pdfFile: [],
      indexingStates: {}, // track loading state per pipeline
      pipelineInfoDialog: false,
      pipelineInfoLoading: false,
      pipelineInfoError: "",
      pipelineInfoData: null,
      pipelineInfoTargetId: null,
      ingestInfoDialog: false,
      ingestInfoTitle: "",
      ingestInfoMessage: "",
      ingestInfoStats: [],
      ingestInfoItems: [],
      savePipelineLoading: false
    };
  },
  async created() {
    await this.$store.dispatch("getCurrentUser");
    await this.$store.dispatch("getNewsApiKeys");
    await this.$store.dispatch("getApiKeys");
    await this.$store.dispatch("getEventsApiKeys");
    await this.$store.dispatch("getKisApiKeys");
    await this.fetchPrompts(); // Load prompts first
    await this.fetchGroups();
    await this.fetchPipelines();
  },
  methods: {
    normalizeBoost(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return 0;
      if (parsed < 0) return 0;
      if (parsed > 0.5) return 0.5;
      return parsed;
    },
    normalizeIncludeURLs(urls) {
      if (!Array.isArray(urls)) return [];
      return urls.map(url => this.normalizeIncludeURL(url)).filter(Boolean);
    },
    normalizeIncludeURL(url) {
      const raw = String(url || "").trim();
      if (!raw) return "";

      const hasWildcard = raw.endsWith("*");
      const base = hasWildcard ? raw.slice(0, -1).trimEnd() : raw;
      const normalizedBase = hasWildcard ? base : base.replace(/\/+$/, "");

      if (!normalizedBase) return hasWildcard ? "*" : "";
      return hasWildcard ? `${normalizedBase}*` : normalizedBase;
    },
    parseIncludeURLsText(text) {
      const value = String(text || "").trim();
      if (!value) return [];
      return value
        .split(/[\n,;]+/)
        .map(url => this.normalizeIncludeURL(url))
        .filter(Boolean);
    },
    isWildcardURL(url) {
      return String(url || "")
        .trim()
        .endsWith("*");
    },
    formatIncludeURL(url) {
      const normalized = String(url || "").trim();
      if (!normalized) return "";
      if (!this.isWildcardURL(normalized)) return normalized;
      const prefix = normalized.slice(0, -1);
      return `${prefix} (Pattern *)`;
    },
    validateWebsitePatternRequirement(includeURLs, sitemap) {
      const hasPattern = includeURLs.some(url => this.isWildcardURL(url));
      if (hasPattern && !sitemap) {
        const message =
          "Wenn ein Include-URL-Pattern (*) verwendet wird, muss eine Sitemap angegeben werden.";
        this.$toast?.error?.(message);
        return false;
      }
      return true;
    },
    async fetchGroups() {
      // API-Call für Gruppen, z.B. /api/eo-prompt/pipeline-groups
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(
          `${env.prompts_api_client_url}api/eo-prompt/pipeline-groups`,
          { params, headers }
        );
        // Resolve prompt names for groups
        this.pipelineGroups = (res.data || []).map(group => {
          if (group.promptShortId) {
            const prompt = this.prompts.find(
              p => p.shortId === group.promptShortId
            );
            return {
              ...group,
              promptName: prompt?.name || ""
            };
          }
          return group;
        });
      } catch (err) {
        console.error("Failed to fetch groups:", err);
      }
    },
    async fetchPrompts() {
      // Lade verfügbare Prompts
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(
          `${env.prompts_api_client_url}api/eo-prompt/prompts`,
          { params, headers }
        );
        this.prompts = res.data || [];
      } catch (err) {
        console.error("Failed to fetch prompts:", err);
        this.prompts = [];
      }
    },
    async openGroupDialog() {
      this.showGroupDialog = true;
      this.newGroupName = "";
      this.newGroupPromptShortId = null;
      this.promptsLoading = true;
      await this.fetchPrompts();
      this.promptsLoading = false;
    },
    async createGroup() {
      if (!this.newGroupName) return;
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const payload = {
          name: this.newGroupName,
          principal_id: this.currentPrincipal,
          promptShortId: this.newGroupPromptShortId
        };
        await axios.post(
          `${env.prompts_api_client_url}api/eo-prompt/pipeline-groups`,
          payload,
          { params, headers }
        );
        this.showGroupDialog = false;
        this.newGroupName = "";
        this.newGroupPromptShortId = null;
        await this.fetchGroups();
      } catch (err) {
        console.error("Failed to create group:", err);
      }
    },
    selectGroup(group) {
      this.selectedGroup = group;
    },
    groupButtonStyle(group) {
      const isActive =
        this.selectedGroup && this.selectedGroup._id === group._id;
      return {
        fontSize: "1.15rem",
        fontWeight: "bold",
        border: "2px solid #00636C",
        color: "#00636C",
        boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
        minWidth: "120px",
        textAlign: "center",
        backgroundColor: isActive ? "#f6f6f6" : "white"
      };
    },
    isPipelineIndexing(id) {
      return !!this.indexingStates[id];
    },
    setPipelineIndexing(id, flag) {
      this.$set(this.indexingStates, id, flag);
    },
    async fetchPipelines() {
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(BASE, { params, headers });
        this.pipelines = (res.data || []).map(pipeline => ({
          ...pipeline,
          // Backward-safe default when backend payload has no cj_enabled yet
          cj_enabled: Boolean(pipeline?.cj_enabled)
        }));
      } catch (err) {
        console.error("Failed to fetch pipelines:", err);
      }
    },

    groupedPipelinesById() {
      const map = {};
      this.pipelineGroups.forEach(group => {
        map[group._id] = {
          ...group,
          pipelines: []
        };
      });
      const ungrouped = {
        _id: "ungrouped",
        name: "Ohne Gruppe",
        pipelines: []
      };
      (this.filteredPipelines || []).forEach(pipeline => {
        if (pipeline.pipeline_group && map[pipeline.pipeline_group]) {
          map[pipeline.pipeline_group].pipelines.push(pipeline);
        } else {
          ungrouped.pipelines.push(pipeline);
        }
      });
      return {
        groups: Object.values(map),
        ungrouped
      };
    },

    async addPipeline() {
      if (!this.form.name || !this.form.type || this.savePipelineLoading)
        return;
      this.savePipelineLoading = true;
      try {
        // PDF-Upload nur bei Typ 'documents' und wenn Dateien vorhanden
        let uploadedFiles = [];
        if (
          this.form.type === "documents" &&
          this.pdfFile &&
          this.pdfFile.length
        ) {
          uploadedFiles = await this.uploadDocument(this.pdfFile);
          if (!uploadedFiles) return; // Upload fehlgeschlagen, Pipeline nicht speichern
        }

        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const isWebsite = this.form.type === "website";
        const includeURLs = isWebsite
          ? Array.from(
              new Set(this.parseIncludeURLsText(this.form.includeURLsText))
            )
          : [];
        const sitemap = isWebsite ? String(this.form.sitemap || "").trim() : "";

        if (
          isWebsite &&
          !this.validateWebsitePatternRequirement(includeURLs, sitemap)
        ) {
          return;
        }

        const payload = {
          name: this.form.name,
          description: this.form.description,
          type: this.form.type,
          sitemap,
          includeURLs,
          rssFeed: this.form.rssFeed,
          boost: this.normalizeBoost(this.form.boost),
          cj_enabled: Boolean(this.form.cj_enabled),
          eoNewsApiKey: this.form.eoNewsApiKey,
          newsBaseURL: this.form.newsBaseURL,
          poiApiKey: this.form.poiApiKey,
          poiBaseURL: this.form.poiBaseURL,
          eventsApiKey: this.form.eventsApiKey,
          eventsBaseURL: this.form.eventsBaseURL,
          kisApiKey: this.form.kisApiKey,
          clubBaseURL: this.form.clubBaseURL,
          committeeBaseURL: this.form.committeeBaseURL,
          departmentBaseURL: this.form.departmentBaseURL,
          facilityBaseURL: this.form.facilityBaseURL,
          personBaseURL: this.form.personBaseURL,
          taskBaseURL: this.form.taskBaseURL,
          tportalSourceURL: this.form.tportalSourceURL,
          thomasBaseURL: this.form.thomasBaseURL,
          documents: uploadedFiles.length ? uploadedFiles : this.form.documents,
          pipeline_group: this.form.pipeline_group
        };

        await axios.post(BASE, payload, { params, headers });
        await this.resetForm();
        await this.fetchPipelines();
      } catch (err) {
        console.error("Failed to add pipeline:", err);
      } finally {
        this.savePipelineLoading = false;
      }
    },

    async editPipeline(pipeline) {
      this.form.name = pipeline.name || "";
      this.form.description = pipeline.description || "";
      this.form.type = pipeline.type || "";
      this.form.sitemap = pipeline.sitemap || "";
      this.form.includeURLsText = this.normalizeIncludeURLs(
        pipeline.includeURLs
      ).join(", ");
      this.form.rssFeed = pipeline.rssFeed || "";
      this.form.boost = this.normalizeBoost(pipeline.boost);
      this.form.cj_enabled = Boolean(pipeline?.cj_enabled);
      this.form.eoNewsApiKey = pipeline.eoNewsApiKey || "";
      this.form.newsBaseURL = pipeline.newsBaseURL || "";
      this.form.poiApiKey = pipeline.poiApiKey || "";
      this.form.poiBaseURL = pipeline.poiBaseURL || "";
      this.form.eventsApiKey = pipeline.eventsApiKey || "";
      this.form.eventsBaseURL = pipeline.eventsBaseURL || "";
      this.form.kisApiKey = pipeline.kisApiKey || "";
      this.form.clubBaseURL = pipeline.clubBaseURL || "";
      this.form.committeeBaseURL = pipeline.committeeBaseURL || "";
      this.form.departmentBaseURL = pipeline.departmentBaseURL || "";
      this.form.facilityBaseURL = pipeline.facilityBaseURL || "";
      this.form.personBaseURL = pipeline.personBaseURL || "";
      this.form.taskBaseURL = pipeline.taskBaseURL || "";
      this.form.tportalSourceURL = pipeline.tportalSourceURL || "";
      this.form.thomasBaseURL = pipeline.thomasBaseURL || "";
      this.form.documents = pipeline.documents || [];
      this.form.pipeline_group = pipeline.pipeline_group || null; // Gruppe setzen
      this.editingId = pipeline._id;
      this.isEditing = true;
      this.showForm = true;
      await this.$nextTick();
      this.scrollToPipelineForm();
    },

    scrollToPipelineForm() {
      const target = this.$refs.pipelineFormSection;
      if (target?.$el?.scrollIntoView) {
        target.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (target?.scrollIntoView) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },

    async copyPipeline(pipeline) {
      await this.resetForm();
      await this.editPipeline(pipeline);
      this.form.name = (pipeline.name || "Untitled") + " (Copy)";
      // new pipeline => not editing mode
      this.isEditing = false;
      this.showForm = true;
      await this.$nextTick();
      this.scrollToPipelineForm();
    },

    async updatePipeline() {
      if (!this.editingId || this.savePipelineLoading) return;
      this.savePipelineLoading = true;
      try {
        let uploadedFiles = [];
        if (
          this.form.type === "documents" &&
          this.pdfFile &&
          this.pdfFile.length
        ) {
          uploadedFiles = await this.uploadDocument(this.pdfFile);
          if (!uploadedFiles) return; // Upload fehlgeschlagen, Pipeline nicht speichern
        }

        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const isWebsite = this.form.type === "website";
        const includeURLs = isWebsite
          ? Array.from(
              new Set(this.parseIncludeURLsText(this.form.includeURLsText))
            )
          : [];
        const sitemap = isWebsite ? String(this.form.sitemap || "").trim() : "";

        if (
          isWebsite &&
          !this.validateWebsitePatternRequirement(includeURLs, sitemap)
        ) {
          return;
        }

        const payload = {
          name: this.form.name,
          description: this.form.description,
          type: this.form.type,
          sitemap,
          includeURLs,
          rssFeed: this.form.rssFeed,
          boost: this.normalizeBoost(this.form.boost),
          cj_enabled: Boolean(this.form.cj_enabled),
          eoNewsApiKey: this.form.eoNewsApiKey,
          newsBaseURL: this.form.newsBaseURL,
          poiApiKey: this.form.poiApiKey,
          poiBaseURL: this.form.poiBaseURL,
          eventsApiKey: this.form.eventsApiKey,
          eventsBaseURL: this.form.eventsBaseURL,
          kisApiKey: this.form.kisApiKey,
          clubBaseURL: this.form.clubBaseURL,
          committeeBaseURL: this.form.committeeBaseURL,
          departmentBaseURL: this.form.departmentBaseURL,
          facilityBaseURL: this.form.facilityBaseURL,
          personBaseURL: this.form.personBaseURL,
          taskBaseURL: this.form.taskBaseURL,
          tportalSourceURL: this.form.tportalSourceURL,
          thomasBaseURL: this.form.thomasBaseURL,
          documents: uploadedFiles.length ? uploadedFiles : this.form.documents,
          pipeline_group: this.form.pipeline_group
        };

        await axios.put(`${BASE}/${this.editingId}`, payload, {
          params,
          headers
        });
        await this.resetForm();
        await this.fetchPipelines();
      } catch (err) {
        console.error("Failed to update pipeline:", err);
      } finally {
        this.savePipelineLoading = false;
      }
    },

    async cancelEdit() {
      await this.resetForm();
    },

    async resetForm() {
      this.form = {
        name: "",
        description: "",
        type: "",
        sitemap: "",
        includeURLsText: "",
        rssFeed: "",
        boost: 0.0,
        cj_enabled: false,
        eoNewsApiKey: "",
        newsBaseURL: "",
        poiApiKey: "",
        poiBaseURL: "",
        eventsApiKey: "",
        eventsBaseURL: "",
        kisApiKey: "",
        clubBaseURL: "",
        committeeBaseURL: "",
        departmentBaseURL: "",
        facilityBaseURL: "",
        personBaseURL: "",
        taskBaseURL: "",
        tportalSourceURL: "",
        thomasBaseURL: "",
        documents: [],
        pipeline_group: null // Gruppe zurücksetzen
      };
      this.editingId = null;
      this.isEditing = false;
      this.showForm = false;
    },

    async confirmDelete(pipeline) {
      this.pipelineToDelete = pipeline;
      this.deleteDialog = true;
    },

    async deletePipeline() {
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        await axios.delete(`${BASE}/${this.pipelineToDelete._id}`, {
          params,
          headers
        });
        this.deleteDialog = false;
        this.pipelineToDelete = null;
        await this.fetchPipelines();
      } catch (err) {
        console.error("Failed to delete pipeline:", err);
      }
    },

    parseIngestItems(ingestResults) {
      if (Array.isArray(ingestResults)) return ingestResults;
      if (Array.isArray(ingestResults?.items)) return ingestResults.items;
      return [];
    },

    sumIngestField(items, field) {
      return items.reduce((sum, item) => sum + Number(item?.[field] || 0), 0);
    },

    buildIngestSuccessMessage(responseData) {
      const ingestResults = responseData?.ingestResults;
      const items = this.parseIngestItems(ingestResults);
      const processedCount = Number(responseData?.count || items.length || 0);

      if (!items.length) {
        if (processedCount > 0) {
          return `Pipeline erfolgreich indiziert (${processedCount} Einträge verarbeitet).`;
        }
        return "Pipeline erfolgreich indiziert.";
      }

      const chunks = this.sumIngestField(items, "chunks");
      const totalOps = this.sumIngestField(items, "totalOps");
      const totalUpserted = this.sumIngestField(items, "totalUpserted");
      const totalModified = this.sumIngestField(items, "totalModified");
      const totalMatched = this.sumIngestField(items, "totalMatched");
      const deletedStale = items.reduce(
        (sum, item) =>
          sum + Number(item?.deleteStaleByHashResult?.deletedCount || 0),
        0
      );
      const deletedTrailing = items.reduce(
        (sum, item) =>
          sum + Number(item?.deleteTrailingByChunkIdResult?.deletedCount || 0),
        0
      );
      const deletedRemovedCount = Number(
        ingestResults && !Array.isArray(ingestResults)
          ? ingestResults?.deletedRemovedCount || 0
          : 0
      );
      const totalDeleted = deletedStale + deletedTrailing + deletedRemovedCount;
      const failedCount = items.filter(item => item?.ok === false).length;

      const parts = [];
      if (processedCount > 0) {
        parts.push(`${processedCount} Einträge verarbeitet`);
      }
      if (chunks > 0) parts.push(`${chunks} Chunks`);
      if (totalOps > 0) parts.push(`${totalOps} Operationen`);
      if (totalUpserted > 0) parts.push(`${totalUpserted} upserted`);
      if (totalModified > 0) parts.push(`${totalModified} modified`);
      if (totalMatched > 0) parts.push(`${totalMatched} matched`);
      if (totalDeleted > 0) parts.push(`${totalDeleted} gelöscht`);
      if (failedCount > 0) parts.push(`${failedCount} fehlgeschlagen`);

      if (!parts.length) return "Pipeline erfolgreich indiziert.";
      return `Pipeline erfolgreich indiziert (${parts.join(", ")}).`;
    },

    buildIngestInfoDialogData(responseData, pipeline) {
      const ingestResults = responseData?.ingestResults;
      const items = this.parseIngestItems(ingestResults);
      const processedCount = Number(responseData?.count || items.length || 0);
      const chunks = this.sumIngestField(items, "chunks");
      const totalOps = this.sumIngestField(items, "totalOps");
      const totalUpserted = this.sumIngestField(items, "totalUpserted");
      const totalModified = this.sumIngestField(items, "totalModified");
      const totalMatched = this.sumIngestField(items, "totalMatched");
      const deletedStale = items.reduce(
        (sum, item) =>
          sum + Number(item?.deleteStaleByHashResult?.deletedCount || 0),
        0
      );
      const deletedTrailing = items.reduce(
        (sum, item) =>
          sum + Number(item?.deleteTrailingByChunkIdResult?.deletedCount || 0),
        0
      );
      const deletedRemovedCount = Number(
        ingestResults && !Array.isArray(ingestResults)
          ? ingestResults?.deletedRemovedCount || 0
          : 0
      );
      const totalDeleted = deletedStale + deletedTrailing + deletedRemovedCount;
      const failedCount = items.filter(item => item?.ok === false).length;
      const source = responseData?.ingested_from || "-";

      const stats = [
        { label: "Pipeline", value: pipeline?.name || "-" },
        { label: "Quelle", value: source },
        { label: "Verarbeitet", value: processedCount },
        { label: "Chunks", value: chunks },
        { label: "Operationen", value: totalOps },
        { label: "Upserted", value: totalUpserted },
        { label: "Modified", value: totalModified },
        { label: "Matched", value: totalMatched },
        { label: "Geloescht (stale/hash)", value: deletedStale },
        { label: "Geloescht (trailing/chunkId)", value: deletedTrailing },
        { label: "Geloescht (removed)", value: deletedRemovedCount },
        { label: "Geloescht (gesamt)", value: totalDeleted },
        { label: "Fehlgeschlagen", value: failedCount }
      ];

      const detailedItems = items.map((item, index) => {
        const stale = Number(item?.deleteStaleByHashResult?.deletedCount || 0);
        const trailing = Number(
          item?.deleteTrailingByChunkIdResult?.deletedCount || 0
        );
        return {
          id: item?.docId || `item-${index + 1}`,
          status: item?.ok === false ? "Fehler" : "OK",
          chunks: Number(item?.chunks || 0),
          upserted: Number(item?.totalUpserted || 0),
          modified: Number(item?.totalModified || 0),
          matched: Number(item?.totalMatched || 0),
          deletedStale: stale,
          deletedTrailing: trailing,
          deletedTotal: stale + trailing
        };
      });

      return {
        title: `Indizierung: ${pipeline?.name || "Pipeline"}`,
        message: this.buildIngestSuccessMessage(responseData),
        stats,
        items: detailedItems
      };
    },

    openIngestInfoDialog(dialogData = {}) {
      this.ingestInfoTitle = dialogData.title || "Info";
      this.ingestInfoMessage = dialogData.message || "";
      this.ingestInfoStats = Array.isArray(dialogData.stats)
        ? dialogData.stats
        : [];
      this.ingestInfoItems = Array.isArray(dialogData.items)
        ? dialogData.items
        : [];
      this.ingestInfoDialog = true;
    },

    closeIngestInfoDialog() {
      this.ingestInfoDialog = false;
      this.ingestInfoTitle = "";
      this.ingestInfoMessage = "";
      this.ingestInfoStats = [];
      this.ingestInfoItems = [];
    },

    async indexPipeline(pipeline) {
      if (!pipeline?._id || this.isPipelineIndexing(pipeline._id)) return;
      this.setPipelineIndexing(pipeline._id, true);
      this.closePipelineInfoDialog();
      this.openIngestInfoDialog({
        title: `Indizierung: ${pipeline?.name || "Pipeline"}`,
        message: "Indizierung wird gestartet...",
        stats: [{ label: "Status", value: "In Bearbeitung" }],
        items: []
      });
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(`${BASE}/${pipeline._id}/ingest`, {
          params,
          headers
        });
        let dialogData;
        try {
          dialogData = this.buildIngestInfoDialogData(res.data, pipeline);
        } catch (parseErr) {
          console.error(
            "Failed to parse ingest response for dialog:",
            parseErr
          );
          dialogData = {
            title: `Indizierung: ${pipeline?.name || "Pipeline"}`,
            message: "Pipeline erfolgreich indiziert.",
            stats: [],
            items: []
          };
        }
        this.openIngestInfoDialog(dialogData);
        this.$toast?.info?.(dialogData.message);
        // Refresh pipelines list to show updated last_indexed timestamp
        await this.fetchPipelines();
      } catch (err) {
        console.error("Failed to ingest pipeline:", err);
        const msg =
          err.response?.data?.error ||
          err.message ||
          "Indizieren fehlgeschlagen.";
        this.openIngestInfoDialog({
          title: `Indizierung: ${pipeline?.name || "Pipeline"}`,
          message: msg,
          stats: [{ label: "Status", value: "Fehlgeschlagen" }],
          items: []
        });
        this.$toast?.error?.(msg);
      } finally {
        this.setPipelineIndexing(pipeline._id, false);
      }
    },

    async openQuestionDialog(group) {
      this.activeGroup = group;
      this.questionText = "";
      this.questionResults = [];
      this.questionError = "";
      this.questionLoading = false;
      this.questionDialog = true;
      this.questionAnswer = "";
    },

    async closeQuestionDialog() {
      this.questionDialog = false;
      this.activeGroup = null;
      this.questionText = "";
      this.questionResults = [];
      this.questionError = "";
      this.questionLoading = false;
      this.questionAnswer = "";
    },
    /*
    async submitQuestion1() {
      if (!this.activePipeline || !this.questionText) return;

      this.questionLoading = true;
      this.questionError = "";
      this.questionResults = [];

      try {
        const headers = getTokenHeader();
        const params = {
          principal_id: this.currentPrincipal
        };

        // POST /pipelines/:id/query
        const res = await axios.post(
          `${BASE}/query`,
          { question: this.questionText, limit: 5 },
          { params, headers }
        );

        // expect: { ok: true, results: [ { text, score, title, url, ... } ] }
        if (res.data && res.data.results) {
          this.questionResults = res.data.results;
        } else {
          this.questionError = "No results returned.";
        }
      } catch (err) {
        console.error("Question query failed:", err);
        this.questionError =
          err.response?.data?.error || err.message || "Request failed";
      } finally {
        this.questionLoading = false;
      }
    }
  
  */
    async submitQuestion() {
      if (!this.activeGroup || !this.questionText) return;

      // Check if group has a prompt assigned
      if (!this.activeGroup.promptShortId) {
        const promptMissingMsg =
          "Dieser Gruppe ist keine Prompt zugewiesen. Bitte bearbeiten Sie die Gruppe und weisen Sie eine Prompt zu.";
        this.questionError = promptMissingMsg;
        this.$toast?.error?.(promptMissingMsg);
        return;
      }

      this.questionLoading = true;
      this.questionError = "";
      this.questionResults = [];
      this.questionAnswer = "";
      try {
        const headers = getTokenHeader();
        const body = {
          principal_id: this.currentPrincipal,
          promptShortId: this.activeGroup.promptShortId,
          payload: this.questionText,
          user: this.currentPrincipal
        };

        const queryRes = await axios.post(
          `${BASE}/query`,
          {
            question: this.questionText,
            groupId: this.activeGroup._id,
            limit: 5
          },
          {
            params: { principal_id: this.currentPrincipal },
            headers
          }
        );
        const results = queryRes.data?.results || [];
        this.questionResults = results;
        let finalPayload = queryRes.data?.prompt_for_llm;
        if (!finalPayload) {
          const ctx = results
            .map((r, idx) => {
              const title = r.title;
              const url = r.url || "";
              const snippet = r.text || "";
              return `[#${idx +
                1}] ${title}\n${snippet} \nQuelle: ${url}`.trim();
            })
            .join("\n\n");
          finalPayload = `FRAGE:\n${this.questionText}\n\nKONTEXT:\n${ctx}\n\nANTWORT:\nBitte beantworte die FRAGE basierend auf dem KONTEXT in klarer, verständlicher Sprache.\nWenn etwas im KONTEXT fehlt, sag offen dass die Information fehlt.`.trim();
        }
        const execRes = await axios.post(
          `${env.prompts_api_client_url}api/eo-prompt/exec`,
          { ...body, payload: finalPayload },
          { headers }
        );
        this.questionAnswer = execRes.data?.text || "";
        if (!this.questionAnswer && (!results || !results.length)) {
          this.questionError = "No results returned.";
          this.$toast?.info?.("Keine Ergebnisse gefunden.");
        }
      } catch (err) {
        console.error("Question query or LLM exec failed:", err);
        this.questionError =
          err.response?.data?.error || err.message || "Request failed";
        this.$toast?.error?.("Anfrage fehlgeschlagen.");
      } finally {
        this.questionLoading = false;
      }
    },

    async uploadDocument(files) {
      if (!files || !files.length) {
        this.$toast?.error?.("Bitte wählen Sie mindestens eine PDF-Datei aus.");
        return false;
      }
      const uploaded = [];
      for (const file of files) {
        if (file.type !== "application/pdf") {
          this.$toast?.error?.("Nur PDF-Dateien sind erlaubt.");
          return false;
        }
        try {
          const res = await uploadFile(
            file,
            this.currentPrincipal,
            "prompts",
            this.currentPrincipal
          );
          uploaded.push({ name: res.title, url: res.url });
        } catch (err) {
          this.$toast?.error?.("Upload fehlgeschlagen.");
          return false;
        }
      }
      this.$toast?.success?.("PDFs erfolgreich hochgeladen.");
      this.pdfFile = [];
      return uploaded;
    },
    async openPipelineInfoDialog(pipeline) {
      if (!pipeline?._id) return;
      this.pipelineInfoDialog = true;
      this.pipelineInfoTargetId = pipeline._id;
      this.pipelineInfoLoading = true;
      this.pipelineInfoError = "";
      this.pipelineInfoData = null;
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const res = await axios.get(`${BASE}/${pipeline._id}/pipeline-info`, {
          params,
          headers
        });
        if (res.data?.pipeline) {
          this.pipelineInfoData = res.data.pipeline;
        } else {
          this.pipelineInfoError = "Keine Informationen gefunden.";
        }
      } catch (err) {
        console.error("Failed to load pipeline info:", err);
        this.pipelineInfoError =
          err.response?.data?.error ||
          err.message ||
          "Info konnte nicht geladen werden.";
      } finally {
        this.pipelineInfoLoading = false;
      }
    },
    closePipelineInfoDialog() {
      this.pipelineInfoDialog = false;
      this.pipelineInfoData = null;
      this.pipelineInfoError = "";
      this.pipelineInfoTargetId = null;
      this.pipelineInfoLoading = false;
    },
    async editGroup(group) {
      this.editingId = group._id;
      this.newGroupName = group.name || "";
      this.newGroupPromptShortId = group.promptShortId || null;
      this.showGroupDialog = true;
      this.promptsLoading = true;
      await this.fetchPrompts();
      this.promptsLoading = false;
    },
    async copyGroup(group) {
      this.editingId = null;
      this.newGroupName = (group.name || "Untitled") + " (Copy)";
      this.newGroupPromptShortId = group.promptShortId || null;
      this.showGroupDialog = true;
      this.promptsLoading = true;
      await this.fetchPrompts();
      this.promptsLoading = false;
    },
    async updateGroup() {
      if (!this.editingId || !this.newGroupName) return;
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        const payload = {
          name: this.newGroupName,
          promptShortId: this.newGroupPromptShortId
        };
        await axios.put(
          `${env.prompts_api_client_url}api/eo-prompt/pipeline-groups/${this.editingId}`,
          payload,
          { params, headers }
        );
        this.showGroupDialog = false;
        this.editingId = null;
        this.newGroupName = "";
        this.newGroupPromptShortId = null;
        await this.fetchGroups();
      } catch (err) {
        console.error("Failed to update group:", err);
      }
    },
    async confirmDeleteGroup(group) {
      this.groupToDelete = group;
      this.deleteGroupDialog = true;
    },
    async deleteGroup() {
      if (!this.groupToDelete?._id) return;
      try {
        const headers = getTokenHeader();
        const params = { principal_id: this.currentPrincipal };
        await axios.delete(
          `${env.prompts_api_client_url}api/eo-prompt/pipeline-groups/${this.groupToDelete._id}`,
          { params, headers }
        );
        this.deleteGroupDialog = false;
        this.groupToDelete = null;
        await this.fetchGroups();
      } catch (err) {
        console.error("Failed to delete group:", err);
      }
    },
    formatDate(date) {
      if (!date) return "—";
      const moment = require("moment");
      return moment(date).format("DD.MM.YYYY HH:mm");
    }
  },
  computed: {
    groupedPipelines() {
      const map = {};
      this.pipelineGroups.forEach(group => {
        map[group._id] = { ...group, pipelines: [] };
      });
      const ungrouped = {
        _id: "ungrouped",
        name: "Ohne Gruppe",
        pipelines: []
      };
      this.filteredPipelines.forEach(pipeline => {
        if (pipeline.pipeline_group && map[pipeline.pipeline_group]) {
          map[pipeline.pipeline_group].pipelines.push(pipeline);
        } else {
          ungrouped.pipelines.push(pipeline);
        }
      });
      const groupedList = Object.values(map);
      if (ungrouped.pipelines.length) {
        groupedList.push(ungrouped);
      }
      return groupedList.length
        ? groupedList
        : [{ _id: "empty", name: "Keine Pipelines", pipelines: [] }];
    },
    filteredPipelines() {
      if (!this.selectedGroup) return this.pipelines;
      return this.pipelines.filter(
        p => p.pipeline_group === this.selectedGroup._id
      );
    },
    currentPrincipal() {
      return this.$store.state.selectedPrincipal.id;
    },
    isAdmin() {
      return this.$store.state.currentUser?.scopes.includes("role:admin");
    },
    isWebsiteSelected() {
      return this.form.type === "website";
    },
    websiteIncludeURLs() {
      if (!this.isWebsiteSelected) return [];
      return this.parseIncludeURLsText(this.form.includeURLsText);
    },
    hasWebsiteIncludePattern() {
      return this.websiteIncludeURLs.some(url => this.isWildcardURL(url));
    },
    isSitemapMissingForPattern() {
      if (!this.hasWebsiteIncludePattern) return false;
      return !String(this.form.sitemap || "").trim();
    },
    sitemapPatternValidationMessage() {
      return this.isSitemapMissingForPattern
        ? "Sitemap ist erforderlich, wenn Include-URL-Pattern (*) verwendet werden."
        : "";
    },
    isRssFeedSelected() {
      return this.form.type === "rss-feed";
    },
    isEONewsSelected() {
      return this.form.type === "eo-news";
    },
    isEOPoisSelected() {
      return this.form.type === "eo-pois";
    },
    isEOEventsSelected() {
      return this.form.type === "eo-events";
    },
    isEOKisSelected() {
      return this.form.type === "eo-kis";
    },
    isTomasSelected() {
      return this.form.type === "tomas";
    },
    isDocumentsSelected() {
      return this.form.type === "documents";
    },
    typeLabelMap() {
      // Für Anzeige in Listen
      return {
        website: "Webseite",
        "rss-feed": "RSS Feed",
        "eo-news": "EO-Nachrichten",
        "eo-pois": "EO-POIs",
        "eo-events": "EO-Events",
        "eo-kis": "EO-KIS",
        tomas: "TOMAS",
        documents: "Dokumente"
      };
    },
    apiKeys() {
      // Return appropriate API keys based on selected type
      let keys = [];
      if (this.form.type === "eo-news") {
        keys = this.$store.state.newsApiKeys;
      } else if (this.form.type === "eo-pois") {
        keys = this.$store.state.apiKeys;
      } else if (this.form.type === "eo-events") {
        keys = this.$store.state.eventsApiKeys;
      } else if (this.form.type === "eo-kis") {
        keys = this.$store.state.kisApiKeys;
      }

      return (keys || []).map(item => {
        const description =
          typeof item.description === "string"
            ? item.description.trim()
            : item.description;

        return {
          ...item,
          description: description || item.apiKey
        };
      });
    }
  },

  watch: {
    currentPrincipal() {
      this.fetchPipelines();
    }
  }
};
